import {
  ZodDefault,
  ZodFormattedError,
  ZodIssue,
  ZodNumber,
  ZodString,
} from 'zod';

import {
  flattenObject,
} from './object';

import {
  IInputNumberProps,
} from '@/components/input-number/input-number';

export type TNumberFiledLimits = {
  max?: number,
  min?: number,
  maxFractionDigits?: number,
};

export type TNumericLimitsProps = Pick<
  IInputNumberProps,
  'minValue' | 'maxValue' | 'minFractionDigits' | 'maxFractionDigits'
>;

export type TStringFieldLimits = {
  minLength?: number,
  maxLength?: number,
};

export type TTextLimitsProps = Partial<
  Pick<HTMLInputElement, 'minLength' | 'maxLength'>
>;

// zod has internal flatten function, but this is what it does:
// {"_errors":[],"auditSpecification":{"_errors":[],"envelopes":{"0":{"_errors":[],"displayName":{"_errors":["Cannot be empty"]}},"_errors":[]}}}
// into this
// {"formErrors":[],"fieldErrors":{"auditSpecification":["Cannot be empty"]}}
//
// whereas we want this:
// {"auditSpecification.envelopes.0.displayName": "Cannot be empty"}
export function flattenZodErrors(errorMap: ZodFormattedError<any>): string[] {
  return Object.entries(flattenObject(errorMap)).map(([
    key,
    value,
  ]) => {
    const newKey = key.split('._errors').shift();
    return `${newKey}: ${value}`;
  });
}

export function formatZodError<T>(error: ZodFormattedError<T>): string {
  function formatError(errorObj: any, path: string[] = []): string {
    if (errorObj._errors?.length) {
      return `${path.join('.')}: ${errorObj._errors.join(', ')}`;
    }
    return Object.entries(errorObj)
      .filter(([
        key,
      ]) => key !== '_errors')
      .map(([
        key,
        value,
      ]) => formatError(value, [
        ...path,
        key,
      ]))
      .join('; ');
  }
  return formatError(error);
}

export function formatZodIssueList(zodIssueList: ZodIssue[]) {
  return zodIssueList.map((_error) => `${_error.path.join('.')}: ${_error.message}`).join('\n');
}

export function getZodNumberLimits(schema: ZodNumber | ZodDefault<ZodNumber>): TNumberFiledLimits {
  const numberSchema = schema instanceof ZodDefault ? schema._def.innerType : schema;
  const limits: TNumberFiledLimits = {};

  for (const check of numberSchema._def.checks) {
    switch (check.kind) {
      case 'min':
        limits.min = check.value;
        break;
      case 'max':
        limits.max = check.value;
        break;
      case 'multipleOf':
        limits.maxFractionDigits = check.value.toString().split('.')?.[1]?.length;
        break;
    }
    if (limits.min !== undefined && limits.max !== undefined && limits.maxFractionDigits !== undefined) {
      break;
    }
  }

  return limits;
}

export function zodNumberLimitsToProps(limits: TNumberFiledLimits): TNumericLimitsProps {
  const props: TNumericLimitsProps = {};
  if (limits.min !== undefined) {
    props.minValue = limits.min;
  }
  if (limits.max !== undefined) {
    props.maxValue = limits.max;
  }
  if (limits.maxFractionDigits !== undefined) {
    props.maxFractionDigits = limits.maxFractionDigits;
    props.minFractionDigits = limits.maxFractionDigits;
  }

  return props;
}

export function getZodLimitsForNumberInputProps(schema: ZodNumber | ZodDefault<ZodNumber>): TNumericLimitsProps {
  return zodNumberLimitsToProps(getZodNumberLimits(schema));
}

export function getZodStringLimits(schema: ZodString): TStringFieldLimits {
  const limits: TStringFieldLimits = {};

  for (const check of schema._def.checks) {
    switch (check.kind) {
      case 'min':
        limits.minLength = check.value;
        break;
      case 'max':
        limits.maxLength = check.value;
        break;
    }
    if (limits.minLength !== undefined && limits.maxLength !== undefined) {
      break;
    }
  }

  return limits;
}

export function zodStringLimitsToProps(limits: TStringFieldLimits): TTextLimitsProps {
  const props: TTextLimitsProps = {};
  if (limits.minLength !== undefined) {
    props.minLength = limits.minLength;
  }
  if (limits.maxLength !== undefined) {
    props.maxLength = limits.maxLength;
  }

  return props;
}

export function getZodLimitsForTextInputProps(schema: ZodString): TTextLimitsProps {
  return zodStringLimitsToProps(getZodStringLimits(schema));
}
